
import { RevButton } from '@ds/components/Button'
import { RevContainer } from '@ds/components/Container'
import { RevInputSelect } from '@ds/components/InputSelect'
import { RevStickyBar } from '@ds/components/StickyBar'
import { RevTag } from '@ds/components/Tag'
import { IconEqualizer } from '@ds/icons/IconEqualizer'
import { IconSort } from '@ds/icons/IconSort'
import isEmpty from 'lodash/isEmpty'
import throttle from 'throttleit'
import { mapGetters, mapState } from 'vuex'

import Pagination from '@core/components/Pagination'
import { pagination } from '@core/components/Pagination/utils'
import { ROUTES } from '@router'
import { trackClick } from '@tracking/events'

import Titles from '../../pages/ProductLanding/components/Titles/Titles.vue'
import SearchParams from '../../utils/SearchParams'
import { getValueTranslation } from '../../utils/SortText'
import ActiveFilters from '../Filters/ActiveFilters'
import Refinements from '../Filters/Refinements'
import LandingNavigation from '../LandingNavigation'

import translations from './ProductResults.translations'
import ProductResults from './ProductResults.vue'

const MOBILE_HEADER_HEIGHT = 75
export const AUTOSCROLL_THRESHOLD = 6

export default {
  components: {
    ActiveFilters,
    ProductResults,
    RevButton,
    RevStickyBar,
    RevTag,
    RevInputSelect,
    Refinements,
    Pagination,
    LandingNavigation,
    RevContainer,
    Titles,
  },
  props: {
    filters: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      showMobileFilterButton: false,
    }
  },

  computed: {
    ...mapState({
      isFilterInModal: (state) => state.layout.breakpoint <= 2,
    }),
    ...mapGetters({
      experiments: 'flags/getExperiments',
      filterWhitelist: 'landingSearch/filterWhitelist',
      landingId: 'landing/landingId',
      landingSeoNavigation: 'landing/landingSeoNavigation',
      landingTagline: 'landing/tagline',
      landingTitle: 'landing/title',
      landingType: 'landing/type',
      searchCurrentPage: 'landingSearch/currentPage',
      searchNbPages: 'landingSearch/nbPages',
      searchNbResults: 'landingSearch/nbResults',
      sortDefaultValue: 'landingSearch/sortDefaultValue',
      sortList: 'landingSearch/sortList',
      isLoading: 'landingSearch/isLoading',
    }),
    IconEqualizer: () => IconEqualizer,
    IconSort: () => IconSort,
    translations: () => translations,
    sortValue() {
      return this.filters.sort ? this.filters.sort[0] : this.sortDefaultValue
    },
    locationFilter() {
      return {
        name: 'filters',
        query: {
          ...this.$route.query,
          from: this.$route.path,
        },
        hash: this.$route.hash,
      }
    },

    locationSort() {
      return {
        name: 'sort',
        query: {
          ...this.$route.query,
          from: this.$route.path,
        },
        hash: this.$route.hash,
      }
    },

    sortOptions() {
      return this.sortList
        .map(({ title, name }) => {
          const translationkey = getValueTranslation(title)

          return {
            label: translationkey ? this.$t(translationkey) : '',
            value: name,
          }
        })
        .filter(({ label }) => label !== '')
    },

    isSearchLanding() {
      return this.$route.name === ROUTES.LANDING.SEARCH
    },

    isProductLanding() {
      return this.$route.name === ROUTES.LANDING.PRODUCT
    },

    hasLandingNavigation() {
      return !isEmpty(this.landingSeoNavigation)
    },

    activeFacetsCount() {
      return this.searchParams.activeFacetsCount
    },

    searchParams() {
      return new SearchParams({
        query: this.$route.query,
        hash: this.$route.hash,
        paramsWhitelist: this.filterWhitelist,
      })
    },

    isTopSectionHidden() {
      if (this.isProductLanding) {
        return this.searchParams.hasUserFilters()
      }

      return false
    },

    displaySearchTitle() {
      return this.isSearchLanding
    },

    displayLandingTitle() {
      return !this.isSearchLanding
    },

    hasPagination() {
      return !isEmpty(pagination(this.searchCurrentPage, this.searchNbPages))
    },

    shouldDisplayFilterButton() {
      return this.experiments.blackFiltersMweb === 'blackFiltersMweb'
    },

    shouldDisplayStickyBar() {
      return !this.shouldDisplayFilterButton
    },

    shouldDisplayStickyFilterButton() {
      return this.shouldDisplayFilterButton && this.showMobileFilterButton
    },
  },

  watch: {
    searchNbResults(newValue) {
      if (newValue < AUTOSCROLL_THRESHOLD) {
        this.scrollToProductList()
      }
    },
  },

  mounted() {
    // Whenever a facet, sorting, pagination... value is set, show the filters right when the
    // page loads. Note that we can't move this to `data` because of client/server mismatch.
    if (this.searchParams.hasUserFilters()) {
      this.showMobileFilterButton = true
    }

    window.addEventListener('scroll', this.showFiltersIfResultsInViewPort)
    // Show the filters before scrolling, if products are visible.
    this.showFiltersIfResultsInViewPort()
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.showFiltersIfResultsInViewPort)
  },
  methods: {
    updateSort(sort) {
      if (this.sortDefaultValue !== sort) {
        this.filter({ sort })

        return
      }

      this.filter({ sort: null })
    },
    onResetFilters() {
      this.$emit('reset-filters')
    },
    onPageChange() {
      this.scrollToProductList()
    },
    filter(value) {
      this.$emit('filter', value)
    },

    scrollToProductList() {
      if (this.$refs.ProductResults && this.$refs.topSection) {
        window.scrollTo(
          0,
          this.$refs.ProductResults.offsetTop - MOBILE_HEADER_HEIGHT,
        )
      }
    },

    showFiltersIfResultsInViewPort: throttle(
      function throttleShowFiltersIfResultsInViewPort() {
        // At this point we're not guaranteed the element still exists so,
        // aborting if it's gone.
        if (!this.$refs.ProductResults) {
          return
        }

        const { bottom } = this.$refs.ProductResults.getBoundingClientRect()

        // When the user scrolls past the results list, let's hide the buttons.
        if (bottom <= 0) {
          this.showMobileFilterButton = false
        } else {
          this.showMobileFilterButton = true
        }
      },
    ),

    trackFilterClick() {
      trackClick({
        name: 'filter',
        zone: 'mobileFilter',
      })
    },

    trackSortClick() {
      trackClick({
        name: 'sort',
        zone: 'mobileSort',
      })
    },
  },
}
